import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  AlertCircle,
  CheckCircle,
  Globe,
  Mail,
  MessageCircle,
  Phone,
  Plus,
  Settings,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const integrations = [
  {
    id: 'website',
    name: 'Website Chat',
    icon: Globe,
    description: 'Live chat widget for your website',
    status: 'connected',
    path: 'chat-integration',
    leads: 45,
    config: {
      widgetColor: '#030213',
      position: 'bottom-right',
      welcomeMessage: 'Hi! How can we help you today?',
    },
  },
  {
    id: 'whatsapp',
    name: 'WhatsApp Business',
    icon: MessageCircle,
    description: 'Connect WhatsApp Business API',
    status: 'disconnected',
    leads: 0,
    config: {
      phoneNumber: '',
      apiKey: '',
      webhookUrl: '',
    },
  },
  {
    id: 'telegram',
    name: 'Telegram Bot',
    icon: Phone,
    description: 'Automated Telegram customer support',
    status: 'pending',
    leads: 12,
    config: {
      botToken: '123456789:AAH...',
      webhookUrl: '',
      autoResponse: true,
    },
  },
  {
    id: 'facebook',
    name: 'Facebook Messenger',
    icon: MessageCircle,
    description: 'Facebook page messaging',
    status: 'disconnected',
    leads: 0,
    config: {
      pageId: '',
      accessToken: '',
      verifyToken: '',
    },
  },
  {
    id: 'email',
    name: 'Email Support',
    icon: Mail,
    description: 'Email-to-chat conversion',
    status: 'connected',
    leads: 23,
    config: {
      supportEmail: 'support@homedecor.com',
      autoReply: true,
      signature: 'Best regards,\nHomeDecor Support Team',
    },
  },
];

export function Integration() {
  const navigate = useNavigate();

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'connected':
        return (
          <Badge className="bg-green-100 text-green-800">
            <CheckCircle className="h-3 w-3 mr-1" />
            Connected
          </Badge>
        );
      case 'pending':
        return (
          <Badge className="bg-yellow-100 text-yellow-800">
            <AlertCircle className="h-3 w-3 mr-1" />
            Pending
          </Badge>
        );
      case 'disconnected':
        return (
          <Badge className="bg-gray-100 text-gray-800">Disconnected</Badge>
        );
      default:
        return <Badge>{status}</Badge>;
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleConfigure = (integration: any) => {
    navigate(integration.path);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1>Omnichannel Integration</h1>
        <Button>
          <Plus className="h-4 w-4 mr-2" />
          Add Integration
        </Button>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Active Channels
            </CardTitle>
            <CheckCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {integrations.filter((i) => i.status === 'connected').length}
            </div>
            <p className="text-xs text-muted-foreground">
              of {integrations.length} configured
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Leads</CardTitle>
            <MessageCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {integrations.reduce((sum, i) => sum + i.leads, 0)}
            </div>
            <p className="text-xs text-muted-foreground">This month</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Best Performer
            </CardTitle>
            <Globe className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">Website</div>
            <p className="text-xs text-muted-foreground">45 leads</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending Setup</CardTitle>
            <AlertCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {
                integrations.filter(
                  (i) => i.status === 'pending' || i.status === 'disconnected'
                ).length
              }
            </div>
            <p className="text-xs text-muted-foreground">
              Channels to configure
            </p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="channels" className="space-y-4">
        <TabsList>
          <TabsTrigger value="channels">Channels</TabsTrigger>
          <TabsTrigger value="settings">Global Settings</TabsTrigger>
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
        </TabsList>

        <TabsContent value="channels">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {integrations.map((integration) => {
              const Icon = integration.icon;
              return (
                <Card key={integration.id} className="relative">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-muted rounded-lg">
                          <Icon className="h-5 w-5" />
                        </div>
                        <div>
                          <CardTitle className="text-base">
                            {integration.name}
                          </CardTitle>
                          <p className="text-sm text-muted-foreground">
                            {integration.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <span className="text-sm">Status</span>
                        {getStatusBadge(integration.status)}
                      </div>

                      <div className="flex items-center justify-between">
                        <span className="text-sm">Leads this month</span>
                        <span className="font-medium">{integration.leads}</span>
                      </div>

                      <div className="flex space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1"
                          onClick={() => handleConfigure(integration)}
                        >
                          <Settings className="h-4 w-4 mr-2" />
                          Configure
                        </Button>
                        {integration.status === 'connected' ? (
                          <Button variant="destructive" size="sm">
                            Disconnect
                          </Button>
                        ) : (
                          <Button size="sm">Connect</Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="settings">
          <Card>
            <CardHeader>
              <CardTitle>Global Integration Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <Label>Lead Routing</Label>
                  <p className="text-sm text-muted-foreground">
                    Automatically route leads to available agents
                  </p>
                </div>
                <Switch defaultChecked />
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <Label>Auto-Response</Label>
                  <p className="text-sm text-muted-foreground">
                    Send automatic responses during off-hours
                  </p>
                </div>
                <Switch defaultChecked />
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <Label>Lead Notifications</Label>
                  <p className="text-sm text-muted-foreground">
                    Get notified when new leads arrive
                  </p>
                </div>
                <Switch defaultChecked />
              </div>

              <div className="space-y-2">
                <Label>Business Hours</Label>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm">Opening Time</Label>
                    <Input type="time" defaultValue="09:00" />
                  </div>
                  <div>
                    <Label className="text-sm">Closing Time</Label>
                    <Input type="time" defaultValue="18:00" />
                  </div>
                </div>
              </div>

              <Button>Save Settings</Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="analytics">
          <Card>
            <CardHeader>
              <CardTitle>Channel Performance</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {integrations.map((integration) => (
                  <div
                    key={integration.id}
                    className="flex items-center justify-between p-4 border rounded-lg"
                  >
                    <div className="flex items-center space-x-3">
                      <integration.icon className="h-5 w-5" />
                      <span>{integration.name}</span>
                    </div>
                    <div className="flex items-center space-x-4">
                      <div className="text-center">
                        <div className="font-bold">{integration.leads}</div>
                        <div className="text-xs text-muted-foreground">
                          Leads
                        </div>
                      </div>
                      <div className="w-20 bg-muted rounded-full h-2">
                        <div
                          className="bg-primary h-2 rounded-full"
                          style={{
                            width: `${(integration.leads / 45) * 100}%`,
                          }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
